type TicketUser = {
  id: number;
  name: string;
};

type TicketPermissions = {
  can_update?: boolean;
  can_delete?: boolean;
  can_accept?: boolean;
  can_reply?: boolean;
};

type TicketPayload = {
  id: number;
  uuid?: string;
  subject: string;
  description: string;
  status: "open" | "in_progress" | "resolved" | "closed";
  priority: number;
  created_by_user_id?: number;
  creator?: TicketUser | null;
  assignee?: TicketUser | null;
  permissions?: TicketPermissions;
  last_message_at?: string | null;
  created_at?: string | null;
  updated_at?: string | null;
  closed_at?: string | null;
  first_response_at?: string | null;
  first_response_due_at?: string | null;
  resolution_due_at?: string | null;
  sla_first_response_breached?: boolean;
  sla_resolution_breached?: boolean;
  escalated_at?: string | null;
  resolution_time_seconds?: number | null;
  resolution_time_human?: string | null;
  unread_count?: number;
  source?: string;
};

type TicketMessagePayload = {
  id: number;
  body?: string | null;
  author?: TicketUser | null;
  created_at?: string | null;
  attachments?: Array<{
    id: number;
    original_name: string;
    mime_type?: string | null;
    size_bytes: number;
    is_image: boolean;
    download_url: string;
  }>;
  is_internal?: boolean;
};

export interface TicketEventMap {
  "ticket-created": {
    ticket_uuid: string;
    ticket_id: number;
    ticket?: TicketPayload;
  };
  "ticket-updated": {
    ticket_uuid: string;
    ticket_id: number;
    ticket?: TicketPayload;
    message?: string;
  };
  "ticket-accepted": {
    ticket_uuid: string;
    ticket_id: number;
    ticket?: TicketPayload;
    message?: string;
    accepted_by_name?: string;
    ticket_subject?: string;
  };
  "ticket-deleted": {
    ticket_uuid: string;
    ticket_id: number;
    message?: string;
  };
  "ticket-message-created": {
    ticket_uuid: string;
    ticket_id: number;
    message: TicketMessagePayload;
    ticket?: TicketPayload;
  };
  "ticket-count-refresh": void;
  "ws-reconnected": void;
}

export type TicketRealtimePayload =
  | TicketEventMap["ticket-created"]
  | TicketEventMap["ticket-updated"]
  | TicketEventMap["ticket-accepted"];

export type TicketMessageRealtimePayload = TicketEventMap["ticket-message-created"];

type Listener<T> = T extends void ? () => void : (payload: T) => void;

const listeners = new Map<string, Set<Function>>();

export const ticketBus = {
  emit<K extends keyof TicketEventMap>(
    event: K,
    ...args: TicketEventMap[K] extends void ? [] : [TicketEventMap[K]]
  ): void {
    const set = listeners.get(event);
    if (!set) return;
    const payload = args[0];
    set.forEach((fn) => fn(payload));
  },

  on<K extends keyof TicketEventMap>(event: K, fn: Listener<TicketEventMap[K]>): () => void {
    let set = listeners.get(event);
    if (!set) {
      set = new Set();
      listeners.set(event, set);
    }
    set.add(fn);
    return () => {
      set!.delete(fn);
      if (set!.size === 0) listeners.delete(event);
    };
  },
};
